import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Typography,
  Button,
  Card,
  Space,
  Modal,
  Tag,
  Descriptions,
  Tooltip,
  Select,
} from 'antd';
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  CalendarOutlined,
  StopOutlined,
} from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import roLocale from '@fullcalendar/core/locales/ro';
import { leaveService } from '../../services/leaveService';
import { departmentService } from '../../services/departmentService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LeaveRequestForm from '../../components/calendar/LeaveRequestForm';
import DepartmentTimeOffOverview from '../../components/calendar/DepartmentTimeOffOverview';
import RejectionModal from '../../components/leave/RejectionModal';
import notify from '../../utils/notify';
import getApiErrorMessage from '../../utils/getApiErrorMessage';
import {
  splitIntoWeekdaySegments,
  buildMonthLegend,
  formatCalendarDate,
  formatCalendarInterval,
} from '../../utils/calendarUtils';
import './CalendarView.css';

const { Title, Text } = Typography;

const CalendarView = () => {
  const { user, canApproveLeaveRequests, canFilterCalendarByDepartment } = useAuth();
  const { t, i18n } = useTranslation();
  const canApproveRequests =
    canApproveLeaveRequests?.() ||
    user?.roles?.some((role) => ['ROLE_ADMIN', 'ROLE_HR', 'ADMIN', 'HR'].includes(role));
  const canFilterByDepartment =
    canFilterCalendarByDepartment?.() ||
    user?.roles?.some((role) => ['ROLE_ADMIN', 'ROLE_HR', 'ROLE_RECEPTION', 'ADMIN', 'HR', 'RECEPTION'].includes(role));
  const [events, setEvents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leaveFormOpen, setLeaveFormOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [rejectRequestId, setRejectRequestId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isMonthView, setIsMonthView] = useState(true);
  const calendarRef = useRef(null);
  const fetchGenRef = useRef(0);

  const holidayDates = useMemo(
    () => new Set(
      events
        .filter(e => e.extendedProps?.type === 'HOLIDAY')
        .map(e => e.start)
    ),
    [events]
  );

  const legendDepartments = useMemo(() => {
    const departments = {};
    events.forEach(event => {
      const dept = event.extendedProps?.departmentName;
      const color = event.extendedProps?.status === 'APPROVED' ? event.backgroundColor : null;
      if (dept && color) {
        departments[dept] = color;
      }
    });
    return departments;
  }, [events]);

  const monthLegend = useMemo(() => buildMonthLegend(events), [events]);

  useEffect(() => {
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!canFilterByDepartment) return;
    const fetchDepartments = async () => {
      try {
        const data = await departmentService.getAll();
        setDepartments(data);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchDepartments();
  }, [canFilterByDepartment]);

  const fetchEvents = async () => {
    // Prevent a stale in-flight response (e.g. a previous month's request
    // that's still pending) from overwriting the state set by a more recent
    // fetchEvents call. Each call captures the generation at invocation time;
    // after the await we discard the result if a newer call has since started.
    const gen = ++fetchGenRef.current;
    try {
      const calendarApi = calendarRef.current?.getApi();
      const currentView = calendarApi?.view;

      let startDate = new Date();
      let endDate = new Date();

      if (currentView) {
        startDate = currentView.currentStart;
        // currentEnd is exclusive; the backend range is inclusive.
        endDate = new Date(currentView.currentEnd);
        endDate.setDate(endDate.getDate() - 1);
      } else {
        // Default to current month
        startDate.setDate(1);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
      }

      // Format the LOCAL calendar date. toISOString() converts to UTC, which in
      // Europe/Bucharest shifts local midnight one day back and silently widens
      // the fetch window into the previous month (surfaced as out-of-month rows
      // in the legend).
      const toLocalIso = (date) =>
        `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      // Intentionally department-agnostic: this FullCalendar + its legend are only
      // shown when no department filter is active (selectedDepartmentId == null).
      // When a department IS selected, this view is replaced by DepartmentTimeOffOverview,
      // which fetches its own department-scoped events. So `events` here is always the
      // all-departments set, and every legend section that reads it is gated on
      // selectedDepartmentId == null — keep that guard on any new legend element.
      const data = await leaveService.getCalendarEvents(
        toLocalIso(startDate),
        toLocalIso(endDate)
      );

      if (gen !== fetchGenRef.current) return;

      const formattedEvents = data.flatMap(event => {
        const base = {
          id: event.type === 'HOLIDAY' ? `holiday-${event.id}` : event.id,
          title: event.title,
          start: event.start,
          end: event.end,
          allDay: true,
          backgroundColor: event.color,
          borderColor: event.color,
          classNames: event.type === 'HOLIDAY' ? ['fc-event-holiday'] : [],
          extendedProps: {
            employeeName: event.employeeName,
            departmentName: event.departmentName,
            leaveType: event.leaveType,
            status: event.status,
            type: event.type,
          },
        };
        // Split leave events into weekday-only segments so the bar never
        // bleeds into Saturday/Sunday columns. Holidays are single-day and kept as-is.
        if (event.type !== 'LEAVE') {
          return [base];
        }
        const segments = splitIntoWeekdaySegments(base);
        return segments.map((segment, index) => ({
          ...segment,
          // Each segment gets a unique id; the original leave request id is
          // kept in extendedProps so approve/reject and the details modal work correctly.
          id: `${base.id}-${index}`,
          extendedProps: {
            ...segment.extendedProps,
            leaveRequestId: event.id,
            // Preserve inclusive original dates for display in the details modal.
            originalStart: event.start,
            originalEnd: event.end,
          },
        }));
      });

      setEvents(formattedEvents);
    } catch (error) {
      if (gen !== fetchGenRef.current) return;
      console.error('Error fetching calendar events:', error);
      notify.error(t('calendar.fetchError'));
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  };

  // FullCalendar fires datesSet on mount and on every view/range change; we
  // refetch and remember whether the month view is active so the legend's
  // per-month sections only render where they make sense.
  const handleDatesSet = (arg) => {
    setIsMonthView(arg.view.type === 'dayGridMonth');
    fetchEvents();
  };

  const handleDateClick = (arg) => {
    if (holidayDates.has(arg.dateStr)) {
      notify.warning(t('calendar.clickedHolidayBlocked'));
      return;
    }
    setSelectedDate(arg.date);
    setLeaveFormOpen(true);
  };

  const handleEventClick = (arg) => {
    // Don't open leave detail modal for holidays
    if (arg.event.extendedProps.type === 'HOLIDAY') {
      return;
    }
    setSelectedEvent(arg.event);
    setEventDetailsOpen(true);
  };

  const handleLeaveRequestSubmit = async (formData) => {
    setFormLoading(true);
    try {
      await leaveService.createRequest(formData);
      notify.success(t('calendar.submitSuccess'));
      setLeaveFormOpen(false);
      fetchEvents();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      notify.error(getApiErrorMessage(error, 'calendar.submitError', t));
    } finally {
      setFormLoading(false);
    }
  };

  const handleApproveReject = async (eventId, status) => {
    try {
      await leaveService.approveOrReject(eventId, { status });
      notify.success(status === 'APPROVED' ? t('calendar.approveSuccess') : t('calendar.rejectSuccess'));
      setEventDetailsOpen(false);
      fetchEvents();
      return true;
    } catch (error) {
      console.error('Error updating leave request:', error);
      notify.error(getApiErrorMessage(error, 'calendar.updateError', t));
    }
  };

  const handleReject = async (rejectionReason) => {
    try {
      await leaveService.approveOrReject(rejectRequestId, {
        status: 'REJECTED',
        rejectionReason,
      });
      notify.success(t('calendar.rejectSuccess'));
      setRejectRequestId(null);
      setEventDetailsOpen(false);
      fetchEvents();
    } catch (error) {
      console.error('Error updating leave request:', error);
      notify.error(getApiErrorMessage(error, 'calendar.updateError', t));
      return false;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const { extendedProps } = event;

    if (extendedProps.type === 'HOLIDAY') {
      return (
        <Tooltip title={t('calendar.bankHolidayTooltip', { title: event.title })}>
          <div
            style={{
              padding: '0.125rem 0.25rem',
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'default',
              fontWeight: 700,
              color: 'white',
            }}
          >
            {event.title}
          </div>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        title={`${extendedProps.employeeName} - ${t(`leave.form.types.${extendedProps.leaveType}`)} (${t(`leave.status.${extendedProps.status}`)})`}
      >
        <div
          style={{
            padding: '0.125rem 0.25rem',
            borderRadius: '0.25rem',
            backgroundColor: event.backgroundColor,
            color: 'white',
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
        >
          {`${extendedProps.employeeName} - ${t(`leave.form.types.${extendedProps.leaveType}`)}`}
          {extendedProps.status === 'PENDING' && (
            <span style={{ marginLeft: '0.25rem', opacity: 0.8 }}>
              {t('calendar.pending')}
            </span>
          )}
        </div>
      </Tooltip>
    );
  };

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Title level={2} style={{ margin: 0 }}>{t('calendar.title')}</Title>
          <Space wrap>
            {canFilterByDepartment && (
              <Select
                placeholder={t('calendar.departmentFilter')}
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ minWidth: 220 }}
                size="large"
                value={selectedDepartmentId}
                onChange={(value) => setSelectedDepartmentId(value ?? null)}
                data-testid="calendar-department-filter"
                options={departments.map((dept) => ({
                  value: dept.id,
                  label: dept.name,
                  display: (
                    <Space>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: dept.colorHex,
                          borderRadius: '50%',
                          display: 'inline-block',
                        }}
                      />
                      {dept.name}
                    </Space>
                  ),
                }))}
                optionRender={(option) => option.data.display}
              />
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setLeaveFormOpen(true)}
              size="large"
              data-testid="request-leave-button"
            >
              {t('calendar.requestLeave')}
            </Button>
          </Space>
        </div>

        {selectedDepartmentId != null ? (
          <>
            <Title level={4} style={{ margin: 0 }}>{t('calendar.overviewTitle')}</Title>
            <DepartmentTimeOffOverview departmentId={selectedDepartmentId} />
          </>
        ) : (
        <Card loading={loading} bodyStyle={{ padding: '1rem', overflowX: 'auto' }}>
          <div className="calendar-wrapper">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek'
              }}
              initialView="dayGridMonth"
              editable={false}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              weekends={true}
              events={events}
              dateClick={handleDateClick}
              eventClick={handleEventClick}
              datesSet={handleDatesSet}
              height="auto"
              eventDisplay="block"
              locale={i18n.language === 'ro' ? roLocale : 'en'}
              firstDay={1}
              eventContent={renderEventContent}
              allDaySlot={true}
              slotMinTime="00:00:00"
              slotMaxTime="24:00:00"
              slotDuration="01:00:00"
              expandRows={true}
              aspectRatio={1.5}
              dayMaxEventRows={false}
              eventMaxStack={999}
              views={{
                timeGridWeek: {
                  dayMaxEventRows: false,
                  dayMaxEvents: false,
                  allDayMaintainDuration: true
                }
              }}
              allDayMaintainDuration={true}
            />
          </div>
        </Card>
        )}

        <Card title={t('calendar.legend')} size="small" data-testid="calendar-legend">
          <Space wrap>
            <Tag color="success" icon={<CheckOutlined />}>
              {t('calendar.approvedLeave')}
            </Tag>
            <Tag color="warning" icon={<CalendarOutlined />}>
              {t('calendar.pendingApproval')}
            </Tag>
            <Tag color="error" icon={<StopOutlined />}>
              {t('calendar.bankHoliday')}
            </Tag>
            {selectedDepartmentId == null &&
              Object.entries(legendDepartments).map(([name, color]) => (
                <Tag
                  key={name}
                  color={color}
                  data-testid={`legend-department-${name}`}
                >
                  {name}
                </Tag>
              ))}
            {events.length === 0 && (
              <Text type="secondary">
                {t('calendar.legendColors')}
              </Text>
            )}
          </Space>

          {selectedDepartmentId == null && isMonthView && monthLegend.holidays.length > 0 && (
            <div style={{ marginTop: '0.75rem' }} data-testid="legend-month-holidays">
              <Text strong>{t('calendar.monthHolidays')}</Text>
              <Space direction="vertical" size={2} style={{ width: '100%', marginTop: 4 }}>
                {monthLegend.holidays.map((holiday) => (
                  <Text
                    key={holiday.date}
                    data-testid={`legend-holiday-${holiday.date}`}
                    style={{ fontSize: '0.8125rem' }}
                  >
                    <StopOutlined style={{ color: 'var(--color-error)', marginRight: 6 }} />
                    {formatCalendarDate(holiday.date, i18n.language)} — {holiday.name}
                  </Text>
                ))}
              </Space>
            </div>
          )}

          {selectedDepartmentId == null && isMonthView && monthLegend.leaves.length > 0 && (
            <div style={{ marginTop: '0.75rem' }} data-testid="legend-month-leaves">
              <Text strong>{t('calendar.employeesOnLeave')}</Text>
              <Space direction="vertical" size={2} style={{ width: '100%', marginTop: 4 }}>
                {monthLegend.leaves.map((leave) => (
                  <Text
                    key={leave.leaveRequestId}
                    data-testid={`legend-leave-${leave.leaveRequestId}`}
                    style={{ fontSize: '0.8125rem' }}
                  >
                    {leave.employeeName}{leave.departmentName ? ` (${leave.departmentName})` : ''}: {formatCalendarInterval(leave.start, leave.end, i18n.language)}
                    {' '}
                    <Text type="secondary" style={{ fontSize: '0.75rem' }}>
                      ({t(`leave.form.types.${leave.leaveType}`)}
                      {leave.status === 'PENDING' ? ` ${t('calendar.pending')}` : ''})
                    </Text>
                  </Text>
                ))}
              </Space>
            </div>
          )}
        </Card>
      </Space>

      <Modal
        title={t('calendar.detailsModal')}
        open={eventDetailsOpen}
        onCancel={() => setEventDetailsOpen(false)}
        footer={[
          <Button key="close" onClick={() => setEventDetailsOpen(false)}>
            {t('common.close')}
          </Button>,
          ...(canApproveRequests && selectedEvent?.extendedProps?.status === 'PENDING' ? [
            <Button
              key="reject"
              danger
              icon={<CloseOutlined />}
              onClick={() => setRejectRequestId(selectedEvent.extendedProps.leaveRequestId ?? selectedEvent.id)}
              data-testid="calendar-reject-leave"
            >
              {t('calendar.reject')}
            </Button>,
            <Button
              key="approve"
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleApproveReject(selectedEvent.extendedProps.leaveRequestId ?? selectedEvent.id, 'APPROVED')}
            >
              {t('calendar.approve')}
            </Button>
          ] : [])
        ]}
        width="min(600px, 95vw)"
      >
        {selectedEvent && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('calendar.employee')}>
              <Text strong>{selectedEvent.extendedProps.employeeName}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={t('calendar.department')}>
              <Tag
                color={selectedEvent.backgroundColor}
                style={{
                  borderColor: selectedEvent.backgroundColor,
                  backgroundColor: `${selectedEvent.backgroundColor}20`,
                  color: selectedEvent.backgroundColor,
                }}
              >
                {selectedEvent.extendedProps.departmentName}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('calendar.leaveType')}>
              {t(`leave.form.types.${selectedEvent.extendedProps.leaveType}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('calendar.duration')}>
              {formatCalendarInterval(
                new Date((selectedEvent.extendedProps.originalStart ?? selectedEvent.start) + 'T00:00:00Z').toISOString().slice(0, 10),
                new Date((selectedEvent.extendedProps.originalEnd ?? selectedEvent.end) + 'T00:00:00Z').toISOString().slice(0, 10),
                i18n.language,
                { withYear: true }
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('calendar.status')}>
              <Tag color={getStatusColor(selectedEvent.extendedProps.status)}>
                {t(`leave.status.${selectedEvent.extendedProps.status}`)}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <RejectionModal
        open={rejectRequestId != null}
        onCancel={() => setRejectRequestId(null)}
        onConfirm={handleReject}
      />

      <LeaveRequestForm
        open={leaveFormOpen}
        onClose={() => setLeaveFormOpen(false)}
        onSubmit={handleLeaveRequestSubmit}
        selectedDate={selectedDate}
        loading={formLoading}
        bankHolidays={holidayDates}
      />
    </div>
  );
};

export default CalendarView;
